import React, { useMemo, useState } from 'react'
import { Modal } from '@mui/material'
import { Formik, Form } from 'formik'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { UpdateContractorFile } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { EditContractorFileNameSchema } from './EditContractorFileNameForm.validator'
import { EditContractorFileNameForm } from './EditContractorFileNameForm.interface'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { ContractorFile } from 'src/interfaces/file'

interface Props {
  isOpen: boolean
  contractorId: string
  contractorFile: ContractorFile
  onClose(): void
  onUpdate(): void | Promise<void>
}

export const EditContractorFileNameModal = ({
  isOpen,
  contractorId,
  contractorFile,
  onClose,
  onUpdate,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const initialValues: EditContractorFileNameForm = useMemo(
    () => ({
      name: contractorFile.name || '',
    }),
    [contractorFile.name],
  )

  const handleSubmit = async (values: EditContractorFileNameForm) => {
    try {
      setIsLoading(true)

      await createAsyncAction(
        dispatch,
        UpdateContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
          name: values.name,
        }),
      )

      showToast('File name has been updated!')

      onClose()
      await onUpdate()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="file-modal-title"
      aria-describedby="file-modal-description"
      css={tw`fixed inset-0 flex items-center justify-center`}
    >
      <div
        data-testid="contractor-details-edit-file-name-modal"
        css={tw`w-[336px] bg-white flex flex-col rounded-lg shadow-lg`}
      >
        <ModalHeader
          className={tw`h-11`}
          title="Edit Name"
          onClose={onClose}
          closeButtonDataTestId="contractor-details-edit-file-name-modal-close-btn"
        />
        <Formik
          initialValues={initialValues}
          validationSchema={EditContractorFileNameSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form css={tw`flex flex-col flex-1`}>
              <div css={tw`p-4 flex-1 overflow-y-auto`}>
                <FormikTextField
                  name="name"
                  fullWidth
                  label="Name"
                  placeholder="Enter custom name"
                />
              </div>
              <div
                css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
              >
                <Button
                  variant="contained"
                  color="grey"
                  size="small"
                  onClick={onClose}
                  loading={isLoading}
                  className={tw`w-[100px]`}
                  data-testid="contractor-details-edit-file-name-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  loading={isLoading}
                  className={tw`w-[120px]`}
                  data-testid="contractor-details-edit-file-name-save-btn"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
